// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Shubham Dixit <shubham@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Users, ChevronLeft, ChevronRight, AlertCircle, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import type { Patient } from '@/types'
import { PREP_STATUS_LABEL } from '@/types'
import { apiFetch } from '@/lib/api'
import { formatDisplayDateTime, formatDobDisplay } from '@/lib/date-format'
import { useAuth } from '@/context/auth-context'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 5

const STATUS_BORDER: Record<string, string> = {
  completed: 'border-l-2 border-l-emerald-500',
  'in-progress': 'border-l-2 border-l-[#006EF9]',
  unopened: 'border-l-2 border-l-amber-400',
}

interface ApiPatient {
  patientId: string
  firstName: string
  lastName: string
  dateOfBirth: string
  sex?: string
  language?: string
  prepStatus?: Patient['prepStatus']
  /** ISO 8601 timestamp of the last Particle/HIE bundle that landed for this
   *  patient; null/absent = never synced. Drives the Records freshness cell. */
  clinicalDataUpdatedAt?: string | null
}

// Records freshness: data at or under this age reads "fresh" (green); older is
// "stale" (amber). Kept in sync with the backend's re-pull threshold so amber
// means "the next lookup will refresh it".
const FRESH_MAX_DAYS = 7

interface Freshness {
  dot: string
  text: string
  label: string
  title: string
}

/** Map a last-synced timestamp to the Records cell's dot colour + age label. */
function freshness(iso?: string | null): Freshness {
  if (!iso) {
    return {
      dot: 'bg-muted-foreground/40',
      text: 'text-muted-foreground',
      label: 'No sync',
      title: 'No clinical records synced yet',
    }
  }
  const ts = new Date(iso).getTime()
  if (Number.isNaN(ts)) {
    return { dot: 'bg-muted-foreground/40', text: 'text-muted-foreground', label: '—', title: iso }
  }
  const days = Math.floor((Date.now() - ts) / 86_400_000)
  const isFresh = days <= FRESH_MAX_DAYS
  return {
    dot: isFresh ? 'bg-emerald-500' : 'bg-amber-500',
    text: isFresh ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400',
    label: days <= 0 ? 'today' : `${days}d`,
    title: `Records last synced ${formatDisplayDateTime(iso)}`,
  }
}

interface PatientsResponse {
  patients: ApiPatient[]
}

type Status = 'loading' | 'done' | 'error'

export default function PatientsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { actingAsOrg } = useAuth()

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | Patient['prepStatus']>('all')
  const [patients, setPatients] = useState<ApiPatient[]>([])
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState<Status>('loading')
  const [errorMsg, setErrorMsg] = useState('')

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

  // Debounce the search box → committed query; reset to page 1 on change
  useEffect(() => {
    const id = setTimeout(() => {
      setQuery(search.trim())
      setPage(1)
    }, 350)
    return () => clearTimeout(id)
  }, [search])

  const fetchPatients = useCallback(async () => {
    setStatus('loading')
    setErrorMsg('')
    const offset = (page - 1) * PAGE_SIZE
    try {
      const res = await apiFetch(
        `/v1/patients?limit=${PAGE_SIZE}&offset=${offset}&q=${encodeURIComponent(query)}`,
      )
      if (!res.ok) throw new Error(`Server returned ${res.status}`)
      const data = await res.json() as PatientsResponse
      const rows = data.patients ?? []
      setPatients(rows)
      // The list endpoint returns no pagination object; derive a count from the
      // current page so the indicator/controls stay sane (no server-side paging yet).
      setTotal(offset + rows.length + (rows.length === PAGE_SIZE ? 1 : 0))
      setStatus('done')
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Unknown error')
      setStatus('error')
    }
    // actingAsOrg keys the fetch so an operator switching orgs reloads the list
    // (apiFetch already carries the matching X-Org-Context).
  }, [page, query, actingAsOrg?.id])

  useEffect(() => {
    void fetchPatients()
  }, [fetchPatients])

  // After adding/linking a patient, refresh the current page

  const FILTER_TABS: { key: 'all' | Patient['prepStatus']; label: string }[] = [
    { key: 'all',         label: t('patients.filterAll')        },
    { key: 'completed',   label: t('patients.filterCompleted')  },
    { key: 'in-progress', label: t('patients.filterInProgress') },
    { key: 'unopened',    label: t('patients.filterUnopened')   },
  ]

  // Filter the loaded page client-side by the real prepStatus (unset → unopened).
  const visiblePatients =
    statusFilter === 'all'
      ? patients
      : patients.filter(p => (p.prepStatus ?? 'unopened') === statusFilter)

  // Only real fields from GET /v1/patients. Identity (DOB · sex · id) is folded
  // into the patient cell; the fabricated Provider/Appointment/Particle columns
  // are gone. "Records" is the HIE data-freshness cell.
  const COLUMNS = [
    t('patients.colName'),
    'Language',
    t('patients.colPrepStatus'),
    'Records',
  ]

  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const rangeEnd = (page - 1) * PAGE_SIZE + patients.length

  return (
    <div className="flex flex-col gap-5">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <Users className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('patients.pageTitle')}</h1>
          <p className="text-sm text-muted-foreground">{t('patients.pageSubtitle')}</p>
        </div>
      </div>

      {/* Controls */}
      <div className="sticky top-0 z-20 rounded-2xl border border-border bg-card px-3.5 py-3 flex flex-wrap items-center gap-2.5">
        <div className="inline-flex items-center rounded-lg border border-border p-0.5">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.key}
              type="button"
              aria-pressed={statusFilter === tab.key}
              onClick={() => setStatusFilter(tab.key)}
              className={cn(
                'px-3 h-8 rounded-md text-[13px] font-medium transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                statusFilter === tab.key
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="relative flex-1 min-w-[200px] max-w-sm ml-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            type="search"
            className="h-9 pl-9"
            placeholder={t('patients.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-[#006EF9] via-[#02C7C3] to-[#006EF9]" />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead className="sticky top-0 z-10">
              <tr className="bg-secondary">
                  {COLUMNS.map(h => (
                    <th
                      key={h}
                      className="text-left font-mono text-[10px] font-semibold text-muted-foreground uppercase tracking-wide px-3 py-3 whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {status === 'loading' ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      {COLUMNS.map((_, j) => (
                        <td key={j} className="px-3 py-3">
                          <div className="h-4 rounded shimmer" style={{ width: j === 0 ? '70%' : '50%' }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : status === 'error' ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-6 py-12">
                      <div className="flex flex-col items-center gap-2 text-center">
                        <AlertCircle className="h-7 w-7 text-destructive" />
                        <p className="text-sm font-medium text-foreground">Could not load patients</p>
                        <p className="text-xs text-muted-foreground">{errorMsg}</p>
                        <Button variant="outline" size="sm" onClick={() => void fetchPatients()} className="mt-1">
                          Retry
                        </Button>
                      </div>
                    </td>
                  </tr>
                ) : visiblePatients.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-6 py-14">
                      <div className="flex flex-col items-center gap-2 text-center">
                        <div className="h-11 w-11 rounded-xl bg-secondary flex items-center justify-center">
                          <Users className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <p className="text-sm font-medium text-foreground">
                          {query ? `No patients match "${query}"` : t('patients.noResults')}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  visiblePatients.map((patient, i) => {
                    const name = [patient.firstName, patient.lastName].filter(Boolean).join(' ') || '—'
                    const prepStatus = patient.prepStatus ?? 'unopened'
                    const fresh = freshness(patient.clinicalDataUpdatedAt)
                    return (
                      <tr
                        key={patient.patientId}
                        onClick={() => navigate(`/patients/${patient.patientId}`)}
                        className={cn(
                          'cursor-pointer transition-colors hover:bg-secondary/50 border-b border-border last:border-0',
                          i % 2 === 1 && 'bg-secondary/20',
                          STATUS_BORDER[prepStatus],
                        )}
                      >
                        {/* Patient: name + DOB · sex · short id */}
                        <td className="px-3 py-3">
                          <span className="block text-[13px] font-semibold text-foreground truncate max-w-[280px]">
                            {name}
                          </span>
                          <span className="block text-[11px] text-muted-foreground">
                            <span className="capitalize">
                              {formatDobDisplay(patient.dateOfBirth)}
                              {patient.sex ? ` · ${patient.sex.toLowerCase()}` : ''}
                            </span>
                            <span className="font-mono text-muted-foreground/70" title={patient.patientId}>
                              {' · '}{patient.patientId.slice(0, 8)}
                            </span>
                          </span>
                        </td>
                        <td className="px-3 py-3 text-[13px] text-muted-foreground uppercase whitespace-nowrap">
                          {patient.language || '—'}
                        </td>
                        <td className="px-3 py-3">
                          <Badge variant={prepStatus}>{PREP_STATUS_LABEL[prepStatus]}</Badge>
                        </td>
                        {/* Records: HIE data freshness */}
                        <td className="px-3 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center gap-1.5" title={fresh.title}>
                            <span className={`h-1.5 w-1.5 rounded-full ${fresh.dot}`} />
                            <span className={`text-xs font-medium ${fresh.text}`}>{fresh.label}</span>
                          </span>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        {status === 'done' && visiblePatients.length > 0 && (
          <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-card px-4 py-3">
          <span className="text-[13px] text-muted-foreground tabular-nums">
            {total !== 1
              ? t('patients.pageIndicatorPlural', { page, total: totalPages, count: total })
              : t('patients.pageIndicator', { page, total: totalPages, count: total })}
            <span className="sr-only">{rangeStart}–{rangeEnd}</span>
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label={t('patients.previous')}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-40 disabled:pointer-events-none"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNumber => (
              <button
                key={pageNumber}
                type="button"
                aria-current={pageNumber === page ? 'page' : undefined}
                onClick={() => setPage(pageNumber)}
                className={cn(
                  'h-8 min-w-8 px-2 rounded-lg text-[13px] font-medium tabular-nums transition-colors',
                  pageNumber === page
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary',
                )}
              >
                {pageNumber}
              </button>
            ))}
            <button
              type="button"
              aria-label={t('patients.nextPage')}
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-40 disabled:pointer-events-none"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          </div>
        )}
      </div>
    </div>
  )
}
